import { useState, useMemo } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import Button from '../../components/ui/Button';
import { PART_CATEGORIES, PART_UNITS } from '../../constants/partCategories';
import { IconX } from '@tabler/icons-react';

const normalisePartNumber = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export default function AddPartModal({ existingParts, onClose }) {
  const [partNumber, setPartNumber] = useState('');
  const [name, setName] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [category, setCategory] = useState('');
  const [unit, setUnit] = useState('each');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const possibleMatches = useMemo(() => {
    if (!partNumber || partNumber.length < 2) return [];
    const normInput = normalisePartNumber(partNumber);
    return existingParts.filter(p => {
      const normExisting = normalisePartNumber(p.partNumber);
      if (normExisting === normInput) return true;
      if (normExisting.includes(normInput) || normInput.includes(normExisting)) return true;
      return false;
    });
  }, [partNumber, existingParts]);

  const exactDuplicate = possibleMatches.some(p => normalisePartNumber(p.partNumber) === normalisePartNumber(partNumber));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (exactDuplicate) { setError('Part number "' + partNumber + '" already exists.'); return; }
    if (!category) { setError('Pick a category.'); return; }
    setError(''); setSaving(true);
    try {
      await addDoc(collection(db, 'parts'), {
        partNumber: partNumber.trim(),
        normalisedPartNumber: normalisePartNumber(partNumber),
        name: name.trim(),
        manufacturer: manufacturer.trim() || null,
        category,
        unit,
        status: 'published',
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
        createdByEmail: auth.currentUser?.email || null,
      });
      onClose();
    } catch (err) {
      console.error(err);
      setError('Failed to create part: ' + err.message);
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ width: 480, maxHeight: '90vh', overflow: 'auto', background: 'var(--color-bg-primary)', borderRadius: 14, boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '0.5px solid var(--color-border-light)' }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>New part</div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><IconX size={18} stroke={1.5} /></div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Part number *</label>
            <input type="text" value={partNumber} onChange={e => setPartNumber(e.target.value)} placeholder="e.g. RE509672" autoFocus required style={{ fontFamily: 'var(--font-mono)' }} />
          </div>

          {possibleMatches.length > 0 && !exactDuplicate && (
            <div style={{ padding: '10px 12px', background: 'var(--color-info-light)', border: '0.5px solid #B8D4EB', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12 }}>
              <div style={{ color: 'var(--color-info)', fontWeight: 500, marginBottom: 4 }}>Possible matches found</div>
              <div style={{ color: 'var(--color-text-secondary)' }}>Did you mean one of these existing parts?</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {possibleMatches.map(p => <span key={p.id} style={{ padding: '3px 8px', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-full)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>{p.partNumber}</span>)}
              </div>
            </div>
          )}

          {exactDuplicate && (
            <div style={{ padding: '10px 12px', background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12, color: 'var(--color-danger)' }}>
              "{partNumber}" already exists in the parts library.
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Engine oil filter" required />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Manufacturer</label>
            <input type="text" value={manufacturer} onChange={e => setManufacturer(e.target.value)} placeholder="e.g. John Deere, Mann, Shell" />
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Category *</label>
              <select value={category} onChange={e => setCategory(e.target.value)} required>
                <option value="">Pick category...</option>
                {PART_CATEGORIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
            </div>
            <div style={{ flex: '0 0 130px' }}>
              <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Unit *</label>
              <select value={unit} onChange={e => setUnit(e.target.value)} required>
                {PART_UNITS.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
              </select>
            </div>
          </div>

          {error && <div style={{ padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12, marginBottom: 16 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button type="submit" loading={saving} disabled={exactDuplicate}>Create part</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
